import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { verifyAdminToken } from "../../APIClient/apiClient";
import "../../style/page/AdminDashboard.css";
// Level 2 Components
import AnnouncementManager from "./components/AnnouncementManager";
import DonationManager from "./components/DonationManager";
import HomepageManager from "./components/HomepageManager";
import LogostripManager from "./components/LogostripManager";
import PagesManager from "./components/PagesManager";
import PublicationsManager from "./components/PublicationsManager";
import OpenSourceManager from "./components/OpenSourceManager";
import ResearchManager from "./components/ResearchManager";
import WebinarsManager from "./components/WebinarsManager";
import MembersManager from "./components/MembersManager";
import PartnersManager from "./components/PartnersManager";
// Level 1 Components
import AdminManagement from "./components/AdminManagement";
import StatsBoard from "./components/StatsBoard";
import AdminProfile from "./components/AdminProfile";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [activeTab, setActiveTab] = useState("pages");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    verifyToken();
  }, []);

  const verifyToken = async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }

    try {
      const data = await verifyAdminToken(token);
      setAdmin(data);
      // Set default tab based on admin role
      if (data.role === "admin_level_1") {
        setActiveTab("stats");
      } else {
        setActiveTab("pages");
      }
      setLoading(false);
    } catch (error) {
      console.error(error);
      localStorage.removeItem("adminToken");
      navigate("/admin/login");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminRole");
    navigate("/admin/login");
  };

  const handleGoClient = () => {
    window.location.href = "/";
  };

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  if (!admin) {
    return <div className="admin-loading">Admin not found</div>;
  }

  return (
    <div className="admin-dashboard">
      <aside className="admin-sidebar">
        <div className="admin-logo">
          <button className="admin-logo-link" onClick={handleGoClient}>
            VietQubit
          </button>
          <p className="admin-role-badge">{admin.role === "admin_level_1" ? "Admin: Level 1" : "Admin: Level 2"}</p>
        </div>

        <nav className="admin-nav">
          {/* Level 1 Only */}
          {admin.role === "admin_level_1" && (
            <>
              <button className={`admin-nav-item ${activeTab === "stats" ? "active" : ""}`} onClick={() => setActiveTab("stats")}>
                Dashboard
              </button>
              <button className={`admin-nav-item ${activeTab === "admin-mgmt" ? "active" : ""}`} onClick={() => setActiveTab("admin-mgmt")}>
                Account
              </button>
            </>
          )}

          {/* Level 2 Content Management */}
          {admin.role === "admin_level_2" && (
            <>
              <button className={`admin-nav-item ${activeTab === "pages" ? "active" : ""}`} onClick={() => setActiveTab("pages")}>
                Page Content
              </button>
              <button className={`admin-nav-item ${activeTab === "announcements" ? "active" : ""}`} onClick={() => setActiveTab("announcements")}>
                Announcements
              </button>
              <button className={`admin-nav-item ${activeTab === "homepage" ? "active" : ""}`} onClick={() => setActiveTab("homepage")}>
                Homepage Cards
              </button>
              <button className={`admin-nav-item ${activeTab === "logostrip" ? "active" : ""}`} onClick={() => setActiveTab("logostrip")}>
                Logo
              </button>
              <button className={`admin-nav-item ${activeTab === "publications" ? "active" : ""}`} onClick={() => setActiveTab("publications")}>
                Publications
              </button>
              <button className={`admin-nav-item ${activeTab === "opensource" ? "active" : ""}`} onClick={() => setActiveTab("opensource")}>
                Open-source
              </button>
              <button className={`admin-nav-item ${activeTab === "research" ? "active" : ""}`} onClick={() => setActiveTab("research")}>
                Research
              </button>
              <button className={`admin-nav-item ${activeTab === "webinars" ? "active" : ""}`} onClick={() => setActiveTab("webinars")}>
                Webinars
              </button>
              <button className={`admin-nav-item ${activeTab === "members" ? "active" : ""}`} onClick={() => setActiveTab("members")}>
                Members
              </button>
              <button className={`admin-nav-item ${activeTab === "partners" ? "active" : ""}`} onClick={() => setActiveTab("partners")}>
                Partners
              </button>
              <button className={`admin-nav-item ${activeTab === "donations" ? "active" : ""}`} onClick={() => setActiveTab("donations")}>
                Donations
              </button>
            </>
          )}
        </nav>

        <div className="admin-user-info">
          <button
            className="admin-user-link"
            onClick={() => setActiveTab("profile")}
            style={{ width: "100%", marginBottom: "10px", textAlign: "left" }}
          >
            <strong>{admin?.username}</strong>
          </button>
          <button className="admin-logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </aside>

      <main className="admin-content">
        <div className="admin-content-header">
          <h1>
            {activeTab === "stats" && "Website Statistics"}
            {activeTab === "admin-mgmt" && "Admin Management"}
            {activeTab === "profile" && "My Profile"}
            {activeTab === "pages" && "Manage Page Content"}
            {activeTab === "announcements" && "Manage Announcements"}
            {activeTab === "homepage" && "Manage Homepage Cards"}
            {activeTab === "logostrip" && "Manage Logostrip"}
            {activeTab === "publications" && "Manage Publications"}
            {activeTab === "opensource" && "Manage Open Source Projects"}
            {activeTab === "research" && "Manage Research Projects"}
            {activeTab === "webinars" && "Manage Webinars"}
            {activeTab === "members" && "Manage Members"}
            {activeTab === "partners" && "Manage Partners"}
            {activeTab === "donations" && "Manage Donation Projects"}
          </h1>
        </div>

        <div className="admin-content-body">
          {/* Level 1 */}
          {activeTab === "stats" && admin && <StatsBoard adminId={admin.id} />}
          {activeTab === "admin-mgmt" && admin && <AdminManagement adminId={admin.id} />}
          {activeTab === "profile" && admin && <AdminProfile adminId={admin.id} />}

          {/* Level 2 */}
          {activeTab === "pages" && <PagesManager />}
          {activeTab === "announcements" && <AnnouncementManager />}
          {activeTab === "homepage" && <HomepageManager />}
          {activeTab === "logostrip" && <LogostripManager />}
          {activeTab === "publications" && <PublicationsManager />}
          {activeTab === "opensource" && <OpenSourceManager />}
          {activeTab === "research" && <ResearchManager />}
          {activeTab === "webinars" && <WebinarsManager />}
          {activeTab === "members" && <MembersManager />}
          {activeTab === "partners" && <PartnersManager />}
          {activeTab === "donations" && <DonationManager />}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
